// This fixed default is a stopgap, not the final design. Formatting should be locale-specific:
// the intended end state is for production to honour the viewer's own runtime locale, with only
// tests and stories pinning a fixed value (via the `locale` option below) for deterministic
// output. It is fixed here today because the committed reference images this package's visual
// comparison harness checks against bake in this design's lowercase "pm" rendering: if production
// fell through to the runtime's own default locale instead (whatever the host OS or browser
// happens to carry, not reliably Australian), the pixel comparison would become non-deterministic
// across machines. That split between production and tests/stories has not been built yet, so
// this constant still backs both.
const DEFAULT_LOCALE = 'en-AU';

/**
 * Shared with ExpiryClock's own `formatOptions` prop, rather than each declaring the same shape
 * independently: a field added to one would otherwise be silently dropped by the other, since
 * excess property checks do not apply across a variable pass-through.
 */
export type FormatExpiryTimeOptions = {
  locale?: string;
  timeZone?: string;
};

/**
 * Formats when a time-boxed action (for example a magic link) expires as a short local time,
 * for example `2:30 pm`. Returns `undefined` for `null` or an unparseable `expiresAt`, so the
 * caller can omit the clock entirely rather than render `Invalid Date` or a confident wrong time.
 *
 * Deliberately `Date | string`, not `number`: a numeric epoch is ambiguous between seconds and
 * milliseconds, and `new Date()` accepts either without complaint, so passing seconds produces a
 * valid (wrong, 1970-something) date rather than an error. This is the likely mistake, not an
 * exotic one: the magic link's own `exp` claim
 * (`packages/cognito-passwordless-auth/src/magic-link.ts`) is in epoch seconds. A caller holding
 * an epoch value converts it explicitly, for example `new Date(exp * 1000)` for seconds.
 */
export function formatExpiryTime(
  expiresAt: Date | string,
  options?: FormatExpiryTimeOptions,
): string | undefined {
  // A nullable backend field reaches here as `null` at runtime whatever the parameter type
  // promises. `new Date(null)` coerces to the Unix epoch rather than an invalid date, so without
  // this guard it would slip past the Number.isNaN check below and render a confident wrong time.
  if (expiresAt === null) {
    return undefined;
  }

  const date = expiresAt instanceof Date ? expiresAt : new Date(expiresAt);
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }

  return new Intl.DateTimeFormat(options?.locale ?? DEFAULT_LOCALE, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: options?.timeZone,
  }).format(date);
}
